/**
 * ManualKevModal — Add / Edit an "Internal Advisory" (Manual KEV) entry.
 *
 * Four input modes (tabs) accessible from either:
 *   1. The ➕ button next to "Latest exploited vulnerabilities" panel
 *   2. The Admin panel → "Manual KEV" tab
 *
 * Modes:
 *   • form   — fill fields directly
 *   • paste  — dump a raw advisory blob → LLM extracts fields
 *   • url    — fetch remote advisory → LLM extracts fields
 *   • csv    — bulk CSV / JSON upload
 *
 * Uses backend endpoints POST/PATCH /api/kev/manual and /api/kev/manual/parse
 * plus /api/kev/manual/bulk. Auth via existing api.js token wiring.
 */
import { useState, useEffect } from "react";
import { X, Wand2, Loader2, ClipboardPaste, Link2, FileUp, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = {
  cve_id: "",
  vendor: "",
  product: "",
  vulnerability_name: "",
  date_added: "",
  short_description: "",
  required_action: "",
  known_ransomware_use: false,
  notes: "",
  source_url: "",
};

const CVE_RE = /^CVE-\d{4}-\d{4,7}$/i;

export default function ManualKevModal({ open, onClose, onSaved, initialEntry = null }) {
  const editing = Boolean(initialEntry);
  const [mode, setMode] = useState(editing ? "form" : "form");
  const [form, setForm] = useState(EMPTY_FORM);
  const [pasteText, setPasteText] = useState("");
  const [urlValue, setUrlValue] = useState("");
  const [csvText, setCsvText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Hydrate the form when editing an existing entry.
  useEffect(() => {
    if (!open) return;
    if (initialEntry) {
      setForm({
        cve_id:               initialEntry.cve || "",
        vendor:               initialEntry.vendor || "",
        product:              initialEntry.product || "",
        vulnerability_name:   initialEntry.name || "",
        date_added:           initialEntry.date_added || "",
        short_description:    initialEntry.short || "",
        required_action:      initialEntry.action || "",
        known_ransomware_use: Boolean(initialEntry.known_ransomware_use),
        notes:                initialEntry.notes || "",
        source_url:           initialEntry.detail_url || "",
      });
      setMode("form");
    } else {
      setForm(EMPTY_FORM);
      setPasteText("");
      setUrlValue("");
      setCsvText("");
      setMode("form");
    }
  }, [open, initialEntry]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runParse = async ({ text, url }) => {
    setParsing(true);
    try {
      const { data } = await api.post("/kev/manual/parse", { text, url });
      const p = data?.parsed || {};
      // Merge — never wipe user-typed values.
      setForm((f) => ({
        ...f,
        cve_id:               f.cve_id || p.cve_id || "",
        vendor:               f.vendor || p.vendor || "",
        product:              f.product || p.product || "",
        vulnerability_name:   f.vulnerability_name || p.vulnerability_name || "",
        short_description:    f.short_description || p.short_description || "",
        required_action:      f.required_action || p.required_action || "",
        known_ransomware_use: f.known_ransomware_use || Boolean(p.known_ransomware_use),
        date_added:           f.date_added || p.date_added || "",
        source_url:           f.source_url || url || "",
      }));
      setMode("form");
      toast.success(data?.ai_enriched ? "Parsed with AI · review then Save." : "Parsed · review then Save.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Parse failed.");
    } finally { setParsing(false); }
  };

  const runBulk = async () => {
    const t = csvText.trim();
    if (!t) return toast.error("Paste CSV or JSON first.");
    setSaving(true);
    try {
      let body;
      // Try JSON first, fall back to CSV.
      try {
        const parsed = JSON.parse(t);
        body = Array.isArray(parsed) ? { items: parsed } : { items: parsed.items || [] };
      } catch {
        body = { csv: t };
      }
      const { data } = await api.post("/kev/manual/bulk", body);
      toast.success(`Imported ${data.added} · skipped ${data.skipped}.`);
      setCsvText("");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed.");
    } finally { setSaving(false); }
  };

  const runSave = async () => {
    if (!CVE_RE.test((form.cve_id || "").toUpperCase())) {
      return toast.error("CVE ID must look like CVE-YYYY-NNNN.");
    }
    setSaving(true);
    try {
      if (editing && initialEntry?.id) {
        await api.patch(`/kev/manual/${initialEntry.id}`, form);
        toast.success("Advisory updated.");
      } else {
        await api.post("/kev/manual", form);
        toast.success("Advisory added.");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const TABS = editing
    ? [{ key: "form", label: "Edit", icon: Pencil }]
    : [
        { key: "form",  label: "Manual form", icon: Pencil },
        { key: "paste", label: "Paste advisory", icon: ClipboardPaste },
        { key: "url",   label: "Fetch URL", icon: Link2 },
        { key: "csv",   label: "Bulk CSV/JSON", icon: FileUp },
      ];

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="manual-kev-modal">
      <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {editing ? "Edit Internal Advisory" : "Add Internal Advisory"}
            </h3>
            <p className="text-xs text-slate-500">
              {editing ? "Update fields for this manually-added KEV entry." : "Publish a manual KEV entry — supersedes CISA row for the same CVE."}
            </p>
          </div>
          <button onClick={onClose} data-testid="manual-kev-close" className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!editing && (
          <div className="flex items-center gap-1 px-3 pt-2 border-b border-slate-100 bg-slate-50">
            {TABS.map((t) => {
              const Icon = t.icon;
              const on = mode === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setMode(t.key)}
                  data-testid={`manual-kev-tab-${t.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-colors ${
                    on ? "border-[#2E7DF5] text-[#2E7DF5]" : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {mode === "form" && (
            <div className="grid grid-cols-2 gap-3 text-sm" data-testid="manual-kev-form">
              <Field label="CVE ID *" required>
                <input value={form.cve_id} onChange={(e) => set("cve_id", e.target.value.toUpperCase())} placeholder="CVE-2024-12345" className={inputCls} data-testid="mk-cve" />
              </Field>
              <Field label="Vulnerability name">
                <input value={form.vulnerability_name} onChange={(e) => set("vulnerability_name", e.target.value)} placeholder="e.g. Path traversal" className={inputCls} data-testid="mk-name" />
              </Field>
              <Field label="Vendor">
                <input value={form.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder="e.g. Cisco" className={inputCls} data-testid="mk-vendor" />
              </Field>
              <Field label="Product">
                <input value={form.product} onChange={(e) => set("product", e.target.value)} placeholder="e.g. NX-OS" className={inputCls} data-testid="mk-product" />
              </Field>
              <Field label="Date added">
                <input type="date" value={form.date_added} onChange={(e) => set("date_added", e.target.value)} className={inputCls} data-testid="mk-date" />
              </Field>
              <Field label="Source URL">
                <input value={form.source_url} onChange={(e) => set("source_url", e.target.value)} placeholder="https://…" className={inputCls} data-testid="mk-url" />
              </Field>
              <Field label="Short description" span2>
                <textarea rows={2} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className={textareaCls} data-testid="mk-short" />
              </Field>
              <Field label="Required action" span2>
                <textarea rows={2} value={form.required_action} onChange={(e) => set("required_action", e.target.value)} className={textareaCls} data-testid="mk-action" />
              </Field>
              <Field label="Notes" span2>
                <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={textareaCls} data-testid="mk-notes" />
              </Field>
              <label className="col-span-2 inline-flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={form.known_ransomware_use} onChange={(e) => set("known_ransomware_use", e.target.checked)} data-testid="mk-ransomware" />
                Known ransomware campaign use
              </label>
            </div>
          )}

          {mode === "paste" && !editing && (
            <div className="space-y-2" data-testid="manual-kev-paste">
              <div className="text-xs text-slate-600">
                Paste any advisory blob (CISA text, vendor bulletin, blog post, PDF text). Regex will pull the CVE id first; Gemini fills in vendor, product, description and action.
              </div>
              <textarea rows={9} value={pasteText} onChange={(e) => setPasteText(e.target.value)} placeholder="Paste advisory text here…" className={textareaCls} data-testid="mk-paste-text" />
              <button
                onClick={() => runParse({ text: pasteText })}
                disabled={parsing || pasteText.trim().length < 10}
                data-testid="mk-parse-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-[#2E7DF5] text-white hover:bg-[#1e6fdb] disabled:opacity-50"
              >
                {parsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {parsing ? "Parsing…" : "Parse & preview"}
              </button>
            </div>
          )}

          {mode === "url" && !editing && (
            <div className="space-y-2" data-testid="manual-kev-url">
              <div className="text-xs text-slate-600">
                Paste an advisory URL. Backend fetches it, strips markup and runs the same parse pipeline.
              </div>
              <input value={urlValue} onChange={(e) => setUrlValue(e.target.value)} placeholder="https://vendor.example.com/advisory/…" className={inputCls} data-testid="mk-url-input" />
              <button
                onClick={() => runParse({ url: urlValue })}
                disabled={parsing || !/^https?:\/\/.+/i.test(urlValue.trim())}
                data-testid="mk-url-fetch-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-[#2E7DF5] text-white hover:bg-[#1e6fdb] disabled:opacity-50"
              >
                {parsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {parsing ? "Fetching + parsing…" : "Fetch & parse"}
              </button>
            </div>
          )}

          {mode === "csv" && !editing && (
            <div className="space-y-2" data-testid="manual-kev-csv">
              <div className="text-xs text-slate-600">
                Paste CSV rows or a JSON array. Recognised columns: <code>cve_id, vendor, product, vulnerability_name, short_description, required_action, known_ransomware_use, date_added, notes, source_url</code>. Rows without a valid CVE id are skipped.
              </div>
              <textarea rows={9} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder={"cve_id,vendor,product,vulnerability_name\nCVE-2024-1234,Cisco,ASA,Path traversal"} className={`${textareaCls} font-mono text-xs`} data-testid="mk-csv-text" />
              <button
                onClick={runBulk}
                disabled={saving || csvText.trim().length < 5}
                data-testid="mk-bulk-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-[#2E7DF5] text-white hover:bg-[#1e6fdb] disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
                {saving ? "Importing…" : "Import all"}
              </button>
            </div>
          )}
        </div>

        {(mode === "form" || editing) && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
            <button onClick={onClose} className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900">Cancel</button>
            <button
              onClick={runSave}
              disabled={saving}
              data-testid="mk-save-btn"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {editing ? "Save changes" : "Save advisory"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls    = "w-full text-sm rounded border border-slate-300 px-2 py-1.5 focus:border-[#2E7DF5] focus:ring-1 focus:ring-[#2E7DF5] outline-none";
const textareaCls = "w-full text-sm rounded border border-slate-300 px-2 py-1.5 focus:border-[#2E7DF5] focus:ring-1 focus:ring-[#2E7DF5] outline-none";

function Field({ label, children, span2 = false, required = false }) {
  return (
    <label className={`flex flex-col gap-0.5 ${span2 ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}{required ? " " : ""}</span>
      {children}
    </label>
  );
}
